import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Polyline,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaGripVertical, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";

import MenuLateral from "../../Componentes/MenuLateral";
import { projectApi, routingApi } from "../../Services/api";

import "./index.css";

const DEFAULT_CENTER = [-15.2436862, -53.6479108];
const ROUTING_TOKEN_KEY = "routingToken";
const ROUTING_AUTH_PATH =
  process.env.REACT_APP_ROUTING_API_AUTH_PATH || "/auth/signin";
const ROUTING_CLIENT_EMAIL = process.env.REACT_APP_ROUTING_API_EMAIL || "";
const ROUTING_CLIENT_PASSWORD =
  process.env.REACT_APP_ROUTING_API_PASSWORD || "";
const IBGE_MUNICIPIOS_ENDPOINT =
  "https://servicodados.ibge.gov.br/api/v1/localidades/municipios";

function isValidLatLng(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function hasValidCoordinates(point) {
  return isValidLatLng(point?.coordinates?.lat, point?.coordinates?.lng);
}

const Roteamento = () => {
  const navigate = useNavigate();

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [waypoints, setWaypoints] = useState([]);
  const [routePoints, setRoutePoints] = useState([]);
  const [routeSegments, setRouteSegments] = useState([]);
  const [routePolyline, setRoutePolyline] = useState([]);
  const [routeOptimizationData, setRouteOptimizationData] = useState(null);
  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedWaypointId, setDraggedWaypointId] = useState(null);
  const [dragOverWaypointId, setDragOverWaypointId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [routeName, setRouteName] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedRouteStatus, setSelectedRouteStatus] = useState(0);
  const [savingRoute, setSavingRoute] = useState(false);
  const [openingRoute, setOpeningRoute] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedWaypointForOrder, setSelectedWaypointForOrder] = useState(null);
  const [orderInput, setOrderInput] = useState("");
  const [modalStatusFilter, setModalStatusFilter] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [cityCatalog, setCityCatalog] = useState([]);
  const [loadingCityCatalog, setLoadingCityCatalog] = useState(false);
  const [cityCatalogError, setCityCatalogError] = useState("");
  const profile = "driving-car";
  const CITY_NOT_ALLOWED_MESSAGE =
    "Cidade nao permitida, nao consta no cadastro do IBGE.";

  useEffect(() => {
    if (localStorage.getItem("usuarioLogado") === null) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    async function loadCityCatalog() {
      setLoadingCityCatalog(true);
      setCityCatalogError("");

      try {
        const response = await fetch(IBGE_MUNICIPIOS_ENDPOINT);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data : [];

        const parsedCities = list
          .map((item) => {
            const name = item?.nome || "";
            const uf =
              item?.microrregiao?.mesorregiao?.UF?.sigla ||
              item?.regiaoImediata?.regiaoIntermediaria?.UF?.sigla ||
              "";

            if (!name || !uf) {
              return null;
            }

            return {
              id: item?.id || null,
              name,
              uf,
              label: `${name} - ${uf}`,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

        if (!cancelled) {
          setCityCatalog(parsedCities);
        }
      } catch (error) {
        if (!cancelled) {
          setCityCatalog([]);
          setCityCatalogError(
            "Nao foi possivel carregar cidades do IBGE para validacao."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingCityCatalog(false);
        }
      }
    }

    loadCityCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  const mapCenter = useMemo(() => {
    if (
      routePolyline.length > 0 &&
      Array.isArray(routePolyline[0]) &&
      isValidLatLng(routePolyline[0][0], routePolyline[0][1])
    ) {
      return routePolyline[0];
    }

    if (routePoints.length > 0 && hasValidCoordinates(routePoints[0])) {
      const firstPoint = routePoints[0];
      return [firstPoint.coordinates.lat, firstPoint.coordinates.lng];
    }

    return DEFAULT_CENTER;
  }, [routePoints, routePolyline]);

  function normalizeCityValue(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s/-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const cityResolver = useMemo(() => {
    const labelByCompositeKey = new Map();
    const labelsByName = new Map();

    cityCatalog.forEach((city) => {
      const normalizedName = normalizeCityValue(city.name);
      const normalizedUf = normalizeCityValue(city.uf);
      const compositeKey = `${normalizedName}|${normalizedUf}`;

      labelByCompositeKey.set(compositeKey, city.label);

      if (!labelsByName.has(normalizedName)) {
        labelsByName.set(normalizedName, []);
      }

      labelsByName.get(normalizedName).push(city.label);
    });

    function resolve(value) {
      const normalized = normalizeCityValue(value);

      if (!normalized) {
        return null;
      }

      const directMatch = cityCatalog.find(
        (city) => normalizeCityValue(city.label) === normalized
      );

      if (directMatch) {
        return directMatch.label;
      }

      const parts = normalized.split(/[-/,\s]+/).filter(Boolean);
      const maybeUf = parts.length > 0 ? parts[parts.length - 1] : "";

      if (/^[a-z]{2}$/.test(maybeUf)) {
        const namePart = parts.slice(0, -1).join(" ");
        const compositeKey = `${namePart}|${maybeUf}`;
        const byUf = labelByCompositeKey.get(compositeKey);

        if (byUf) {
          return byUf;
        }
      }

      const nameMatches = labelsByName.get(normalized) || [];

      if (nameMatches.length === 1) {
        return nameMatches[0];
      }

      return null;
    }

    return {
      resolve,
      options: cityCatalog.map((city) => city.label),
    };
  }, [cityCatalog]);

  function resolveCityOrNull(value) {
    if (!value || !value.trim()) {
      return null;
    }

    return cityResolver.resolve(value);
  }

  function showValidationAlert(message) {
    Swal.fire({
      icon: "warning",
      title: "Validacao de cidade",
      text: message,
      confirmButtonText: "OK",
    });
  }

  function createWaypoint(address, coordinates = null) {
    return {
      id: `${Date.now()}-${Math.random()}`,
      address,
      coordinates,
    };
  }

  function parseJsonRoute(jsonRoute) {
    if (!jsonRoute) {
      return null;
    }

    if (typeof jsonRoute === "string") {
      try {
        return JSON.parse(jsonRoute);
      } catch (error) {
        return null;
      }
    }

    if (typeof jsonRoute === "object") {
      return jsonRoute;
    }

    return null;
  }

  function decodeEncodedPolyline(polyline, precision = 5) {
    if (typeof polyline !== "string" || !polyline.trim()) {
      return [];
    }

    let index = 0;
    let lat = 0;
    let lng = 0;
    const factor = Math.pow(10, precision);
    const coordinates = [];

    while (index < polyline.length) {
      let result = 0;
      let shift = 0;
      let byte = null;

      do {
        if (index >= polyline.length) {
          return [];
        }
        byte = polyline.charCodeAt(index) - 63;
        index += 1;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index < polyline.length + 1);

      const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += deltaLat;

      result = 0;
      shift = 0;

      do {
        if (index >= polyline.length) {
          return [];
        }
        byte = polyline.charCodeAt(index) - 63;
        index += 1;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index < polyline.length + 1);

      const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += deltaLng;

      coordinates.push([lat / factor, lng / factor]);
    }

    return coordinates;
  }

  function extractGeometryCoordinates(rawGeometry) {
    if (!rawGeometry) {
      return {
        coordinates: [],
        format: null,
      };
    }

    if (typeof rawGeometry === "string") {
      const decodedPolyline5 = decodeEncodedPolyline(rawGeometry, 5);

      if (decodedPolyline5.length > 1) {
        return {
          coordinates: decodedPolyline5,
          format: "latlng",
        };
      }

      const decodedPolyline6 = decodeEncodedPolyline(rawGeometry, 6);

      if (decodedPolyline6.length > 1) {
        return {
          coordinates: decodedPolyline6,
          format: "latlng",
        };
      }

      return {
        coordinates: [],
        format: null,
      };
    }

    if (rawGeometry?.type === "Feature") {
      return extractGeometryCoordinates(rawGeometry.geometry);
    }

    if (rawGeometry?.type === "LineString") {
      return {
        coordinates: Array.isArray(rawGeometry.coordinates)
          ? rawGeometry.coordinates
          : [],
        format: "lnglat",
      };
    }

    if (
      rawGeometry?.geometry &&
      (rawGeometry.geometry.type || Array.isArray(rawGeometry.geometry))
    ) {
      return extractGeometryCoordinates(rawGeometry.geometry);
    }

    if (Array.isArray(rawGeometry)) {
      return {
        coordinates: rawGeometry,
        format: "lnglat",
      };
    }

    return {
      coordinates: [],
      format: null,
    };
  }

  function getRouteDateValue(item) {
    const currentDate =
      item?.dataalteracao ||
      item?.["dataalteração"] ||
      item?.datacriacao ||
      null;
    const parsed = currentDate ? new Date(currentDate).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatDateTime(value) {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("pt-BR");
  }

  function sanitizeFileName(value) {
    return String(value || "rota")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .trim()
      .replace(/\s+/g, "_");
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function downloadFile(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  function splitCityAndUf(address) {
    const normalizedAddress = String(address || "").trim();

    if (!normalizedAddress) {
      return {
        cidade: "-",
        uf: "-",
      };
    }

    const parts = normalizedAddress.split(/\s*-\s*/);

    if (parts.length >= 2) {
      const uf = parts[parts.length - 1].trim().toUpperCase();
      const cidade = parts.slice(0, -1).join(" - ").trim();

      return {
        cidade: cidade || normalizedAddress,
        uf: uf || "-",
      };
    }

    const matchingCity = cityCatalog.find((city) => city.label === normalizedAddress);

    if (matchingCity) {
      return {
        cidade: matchingCity.name || normalizedAddress,
        uf: matchingCity.uf || "-",
      };
    }

    return {
      cidade: normalizedAddress,
      uf: "-",
    };
  }

  async function requestRoutingToken() {
    if (!ROUTING_CLIENT_EMAIL || !ROUTING_CLIENT_PASSWORD) {
      throw new Error(
        "As credenciais da API de roteamento nao foram configuradas no .env."
      );
    }

    const response = await routingApi.post(ROUTING_AUTH_PATH, {
      email: ROUTING_CLIENT_EMAIL,
      password: ROUTING_CLIENT_PASSWORD,
    });

    const token =
      response?.data?.access_token ||
      response?.data?.accessToken ||
      response?.data?.token;

    if (!token) {
      throw new Error(
        "A API de roteamento nao retornou um token em um formato suportado."
      );
    }

    localStorage.setItem(ROUTING_TOKEN_KEY, token);

    return token;
  }

  async function getRoutingToken(forceRefresh = false) {
    const storedToken = localStorage.getItem(ROUTING_TOKEN_KEY);

    if (storedToken && !forceRefresh) {
      return storedToken;
    }

    return requestRoutingToken();
  }

  async function getRequestHeaders(forceRefresh = false) {
    const token = await getRoutingToken(forceRefresh);

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async function sendRoutingRequest(config) {
    try {
      const headers = await getRequestHeaders();

      return await routingApi({
        ...config,
        headers: {
          ...(config.headers || {}),
          ...headers,
        },
      });
    } catch (error) {
      if (error?.response?.status !== 401) {
        throw error;
      }

      localStorage.removeItem(ROUTING_TOKEN_KEY);

      const refreshedHeaders = await getRequestHeaders(true);

      return routingApi({
        ...config,
        headers: {
          ...(config.headers || {}),
          ...refreshedHeaders,
        },
      });
    }
  }

  function buildJsonRoutePayload(status = selectedRouteStatus) {
    return {
      origin,
      destination,
      waypoints: waypoints.map((item) => item.address),
      routePoints,
      routeSegments,
      routePolyline,
      totalDistance,
      totalDuration,
      profile,
      situacao: status,
    };
  }

  function applyLoadedRoute(routeData) {
    const parsedRoute = parseJsonRoute(routeData?.jsonrota);
    const parsedOptimizedRoute = parseJsonRoute(routeData?.jsonrotaotimizada);

    if (!parsedRoute) {
      setErrorMessage("A rota selecionada possui jsonrota invalido.");
      return;
    }

    const loadedOrigin = parsedRoute.origin || "";
    const loadedDestination = parsedRoute.destination || "";
    const loadedWaypoints = Array.isArray(parsedRoute.waypoints)
      ? parsedRoute.waypoints
      : [];
    const loadedPoints = Array.isArray(parsedRoute.routePoints)
      ? parsedRoute.routePoints
      : [];
    const loadedSegments = Array.isArray(parsedRoute.routeSegments)
      ? parsedRoute.routeSegments
      : [];
    const loadedPolyline = Array.isArray(parsedRoute.routePolyline)
      ? parsedRoute.routePolyline
      : [];

    setOrigin(loadedOrigin);
    setDestination(loadedDestination);
    setWaypoints(loadedWaypoints.map((address) => createWaypoint(address)));
    setRoutePoints(loadedPoints);
    setRouteSegments(loadedSegments);
    setRoutePolyline(loadedPolyline);
    setRouteOptimizationData(parsedOptimizedRoute);
    setTotalDistance(parsedRoute.totalDistance || 0);
    setTotalDuration(parsedRoute.totalDuration || 0);
    setRouteName(routeData?.nomerota || "");
    setSelectedRouteId(routeData?.IDrota || null);
    setSelectedRouteStatus(Number(routeData?.situacao) || 0);
    setErrorMessage("");
    setSuccessMessage("Rota carregada com sucesso.");
  }

  async function fetchRoutesByStatus(status) {
    const response = await projectApi.get(`/roteamento/rotas?situacao=${status}`);
    const list = Array.isArray(response?.data) ? response.data : [];

    if (status === 1) {
      return [...list]
        .sort((a, b) => getRouteDateValue(b) - getRouteDateValue(a))
        .slice(0, 30);
    }

    return list;
  }

  async function openRoutesModal() {
    setShowRouteModal(true);
    setModalStatusFilter(0);
    setModalError("");
    setAvailableRoutes([]);
    setModalLoading(true);

    try {
      const list = await fetchRoutesByStatus(0);
      setAvailableRoutes(list);
    } catch (error) {
      setModalError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar as rotas."
      );
    } finally {
      setModalLoading(false);
    }
  }

  async function changeModalFilter(status) {
    if (modalStatusFilter === status) {
      return;
    }

    setModalStatusFilter(status);
    setModalError("");
    setAvailableRoutes([]);
    setModalLoading(true);

    try {
      const list = await fetchRoutesByStatus(status);
      setAvailableRoutes(list);
    } catch (error) {
      setModalError(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar as rotas."
      );
    } finally {
      setModalLoading(false);
    }
  }

  async function handleSelectRoute(routeItem) {
    if (!routeItem?.IDrota) {
      return;
    }

    setOpeningRoute(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await projectApi.get(`/roteamento/rota/${routeItem.IDrota}`);
      applyLoadedRoute(response.data);
      setShowRouteModal(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel carregar os dados da rota selecionada."
      );
    } finally {
      setOpeningRoute(false);
    }
  }

  async function saveOrUpdateRoute(nextStatus) {
    const normalizedName = routeName.trim();

    if (!normalizedName) {
      setErrorMessage("Informe o nome da rota antes de salvar.");
      return false;
    }

    if (!origin.trim() || !destination.trim()) {
      setErrorMessage("Informe origem e destino antes de salvar.");
      return false;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return false;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return false;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin || !resolvedDestination) {
      const message = CITY_NOT_ALLOWED_MESSAGE;
      setErrorMessage(message);
      showValidationAlert(message);
      return false;
    }

    if (resolvedOrigin !== origin) {
      setOrigin(resolvedOrigin);
    }

    if (resolvedDestination !== destination) {
      setDestination(resolvedDestination);
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < waypoints.length; index += 1) {
      const item = waypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        const message = `Parada ${index + 1}: ${CITY_NOT_ALLOWED_MESSAGE}`;
        setErrorMessage(message);
        showValidationAlert(message);
        return false;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    if (
      resolvedWaypoints.some(
        (item, index) => item.address !== waypoints[index]?.address
      )
    ) {
      setWaypoints(resolvedWaypoints);
    }

    const jsonRoutePayload = {
      ...buildJsonRoutePayload(nextStatus),
      origin: resolvedOrigin,
      destination: resolvedDestination,
      waypoints: resolvedWaypoints.map((item) => item.address),
    };

    const payload = {
      nomerota: normalizedName,
      jsonrota: jsonRoutePayload,
      jsonrotaotimizada: routeOptimizationData,
      situacao: nextStatus,
    };

    setSavingRoute(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (selectedRouteId) {
        console.log("[saveOrUpdateRoute] PUT", {
          url: `/roteamento/rota/${selectedRouteId}`,
          payload,
        });
        await projectApi.put(`/roteamento/rota/${selectedRouteId}`, payload);
        setSelectedRouteStatus(nextStatus);
        setSuccessMessage(
          nextStatus === 1
            ? "Rota finalizada e atualizada com sucesso."
            : "Rota alterada com sucesso."
        );
      } else {
        const response = await projectApi.post("/roteamento/rota", payload);
        const createdId = response?.data?.IDrota || null;
        setSelectedRouteId(createdId);
        setSelectedRouteStatus(nextStatus);
        setSuccessMessage(
          nextStatus === 1
            ? "Rota finalizada e salva com sucesso."
            : "Rota salva com sucesso."
        );
      }

      return true;
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.errors?.default ||
          "Nao foi possivel salvar os dados da rota."
      );
      return false;
    } finally {
      setSavingRoute(false);
    }
  }

  async function handleSaveRoute() {
    await saveOrUpdateRoute(selectedRouteStatus === 1 ? 1 : 0);
  }

  async function handleFinishRoute() {
    await saveOrUpdateRoute(1);
  }

  function buildExportRows() {
    if (routePoints.length > 0) {
      return routePoints.map((point, index) => ({
        ...splitCityAndUf(point.address),
        distancia: formatDistance(point.distance_from_previous ?? 0),
        duracao: formatDuration(point.duration_from_previous ?? 0),
      }));
    }

    const addresses = [origin, ...waypoints.map((item) => item.address), destination].filter(
      Boolean
    );

    return addresses.map((address, index) => ({
      ...splitCityAndUf(address),
      distancia: index === 0 ? "0 km" : "-",
      duracao: index === 0 ? "0 min" : "-",
    }));
  }

  function exportRouteAsExcel() {
    const rows = buildExportRows();

    if (rows.length === 0) {
      setErrorMessage("Nao ha dados de rota para exportar.");
      return;
    }

    const csvLines = [
      ["Nome da rota", routeName || "Rota sem nome"].join(";"),
      ["Origem", origin || "-"].join(";"),
      ["Destino", destination || "-"].join(";"),
      "",
      ["Cidade", "UF", "KM ponto anterior", "Tempo ponto anterior"].join(";"),
      ...rows.map((row) =>
        [
          `"${String(row.cidade || "-").replace(/"/g, '""')}"`,
          row.uf,
          row.distancia,
          row.duracao,
        ].join(";")
      ),
      "",
      ["Total em KM", formatDistance(totalDistance)].join(";"),
      ["Tempo total", formatDuration(totalDuration)].join(";"),
    ];

    const blob = new Blob(["\uFEFF" + csvLines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });

    downloadFile(blob, `${sanitizeFileName(routeName || "rota")}.csv`);
    setSuccessMessage("Arquivo Excel gerado com sucesso.");
  }

  function exportRouteAsPdf() {
    const rows = buildExportRows();

    if (rows.length === 0) {
      setErrorMessage("Nao ha dados de rota para exportar.");
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      setErrorMessage("Nao foi possivel abrir a janela para gerar o PDF.");
      return;
    }

    const rowMarkup = rows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.cidade)}</td>
            <td>${escapeHtml(row.uf)}</td>
            <td>${escapeHtml(row.distancia)}</td>
            <td>${escapeHtml(row.duracao)}</td>
          </tr>
        `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(routeName || "Rota")}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #183524; }
            h1 { margin: 0 0 6px; font-size: 24px; }
            p { margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #c8d7cc; padding: 8px; text-align: left; font-size: 12px; }
            th { background: #edf5ee; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(routeName || "Rota sem nome")}</h1>
          <p><strong>Origem:</strong> ${escapeHtml(origin || "-")}</p>
          <p><strong>Destino:</strong> ${escapeHtml(destination || "-")}</p>
          <table>
            <thead>
              <tr>
                <th>Cidade</th>
                <th>UF</th>
                <th>KM ponto anterior</th>
                <th>Tempo ponto anterior</th>
              </tr>
            </thead>
            <tbody>${rowMarkup}</tbody>
          </table>
          <p><strong>Total em KM:</strong> ${escapeHtml(formatDistance(totalDistance))}</p>
          <p><strong>Tempo total:</strong> ${escapeHtml(formatDuration(totalDuration))}</p>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    setSuccessMessage("Visualizacao para PDF aberta com sucesso.");
  }

  async function handleExportRoute() {
    const result = await Swal.fire({
      title: "Gerar arquivo",
      text: "Escolha o formato de exportacao da rota.",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "PDF",
      denyButtonText: "Excel",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      exportRouteAsPdf();
    }

    if (result.isDenied) {
      exportRouteAsExcel();
    }
  }

  function buildOptimizeQuery(
    nextWaypoints = waypoints,
    shouldOptimize = true,
    includeGeometry = true,
    nextOrigin = origin,
    nextDestination = destination
  ) {
    const params = new URLSearchParams();

    params.append("origin", nextOrigin);
    params.append("destination", nextDestination);
    params.append("optimize", shouldOptimize ? "true" : "false");
    params.append("profile", profile);
    params.append("include_geometry", includeGeometry ? "true" : "false");

    nextWaypoints.forEach((item) => {
      params.append("waypoints[]", item.address);
    });

    return params.toString();
  }

  function getOptimizeEndpoint() {
    return "/routes/optimizeplus";
  }

  function formatDistance(distanceInMeters) {
    if (!distanceInMeters) {
      return "0 km";
    }

    return `${(distanceInMeters / 1000).toFixed(1)} km`;
  }

  function formatDuration(durationInSeconds) {
    if (!durationInSeconds) {
      return "0 min";
    }

    const totalMinutes = Math.round(durationInSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
      return `${minutes} min`;
    }

    return `${hours}h ${minutes}min`;
  }

  function isPlaceholderAddress(address) {
    if (!address) {
      return true;
    }

    return /^waypoint\s+\d+$/i.test(address.trim());
  }

  function resolveWaypointAddress(
    address,
    waypointAddresses,
    originAddress,
    destinationAddress
  ) {
    if (!address) {
      return address;
    }

    const trimmedAddress = address.trim();

    if (/^origin$/i.test(trimmedAddress)) {
      return originAddress;
    }

    if (/^destination$/i.test(trimmedAddress)) {
      return destinationAddress;
    }

    if (!isPlaceholderAddress(trimmedAddress)) {
      return address;
    }

    const matchedNumber = trimmedAddress.match(/\d+/);
    const waypointIndex = matchedNumber ? Number(matchedNumber[0]) - 1 : -1;

    if (
      waypointIndex < 0 ||
      waypointIndex >= waypointAddresses.length ||
      !waypointAddresses[waypointIndex]
    ) {
      return address;
    }

    return waypointAddresses[waypointIndex];
  }

  function buildNormalizedOptimizedRoute(optimizedRoute, submittedAddresses) {
    const totalPoints = submittedAddresses.length;

    if (totalPoints === 0) {
      return [];
    }

    if (totalPoints === 1) {
      return [
        {
          address: submittedAddresses[0],
          order: 0,
          coordinates: null,
          distance_from_previous: 0,
          duration_from_previous: 0,
        },
      ];
    }

    const originAddress = submittedAddresses[0];
    const destinationAddress = submittedAddresses[totalPoints - 1];
    const middleAddresses = submittedAddresses.slice(1, -1);
    const orderedMiddle = [];

    optimizedRoute.forEach((item) => {
      if (!item || !item.address) {
        return;
      }

      const resolvedAddress = resolveWaypointAddress(
        item.address,
        middleAddresses,
        originAddress,
        destinationAddress
      );

      if (
        resolvedAddress === originAddress ||
        resolvedAddress === destinationAddress
      ) {
        return;
      }

      if (!middleAddresses.includes(resolvedAddress)) {
        return;
      }

      if (!orderedMiddle.includes(resolvedAddress)) {
        orderedMiddle.push(resolvedAddress);
      }
    });

    middleAddresses.forEach((address) => {
      if (!orderedMiddle.includes(address)) {
        orderedMiddle.push(address);
      }
    });

    return [originAddress, ...orderedMiddle, destinationAddress].map(
      (address, index) => ({
        address,
        order: index,
        coordinates: null,
        distance_from_previous: index === 0 ? 0 : null,
        duration_from_previous: index === 0 ? 0 : null,
      })
    );
  }

  function extractRoutePolyline(data) {
    const rawGeometry =
      data?.geometry || data?.route_geometry || data?.geojson || null;
    const { coordinates, format } = extractGeometryCoordinates(rawGeometry);

    if (!Array.isArray(coordinates)) {
      return [];
    }

    const normalizedCoordinates = coordinates
      .filter(
        (coordinate) =>
          Array.isArray(coordinate) &&
          coordinate.length >= 2 &&
          Number.isFinite(coordinate[0]) &&
          Number.isFinite(coordinate[1])
      );

    if (format === "latlng") {
      return normalizedCoordinates.map((coordinate) => [coordinate[0], coordinate[1]]);
    }

    return normalizedCoordinates.map((coordinate) => [coordinate[1], coordinate[0]]);
  }

  async function fetchRoutePreview(
    nextWaypoints = waypoints,
    shouldOptimize = true
  ) {
    const response = await sendRoutingRequest({
      method: "get",
      url: `${getOptimizeEndpoint()}?${buildOptimizeQuery(
        nextWaypoints,
        shouldOptimize,
        true
      )}`,
    });

    return response.data;
  }

  function buildRouteFromSegments(segmentsResponse) {
    if (!segmentsResponse || segmentsResponse.length === 0) {
      return {
        points: [],
        distance: 0,
        duration: 0,
      };
    }

    const firstSegment = segmentsResponse[0];
    const points = [
      {
        address: firstSegment.origin.address,
        order: 0,
        coordinates: firstSegment.origin.coordinates,
        distance_from_previous: 0,
        duration_from_previous: 0,
      },
    ];

    segmentsResponse.forEach((segment, index) => {
      points.push({
        address: segment.destination.address,
        order: index + 1,
        coordinates: segment.destination.coordinates,
        distance_from_previous: segment.distance,
        duration_from_previous: segment.duration,
      });
    });

    const distance = segmentsResponse.reduce(
      (total, segment) => total + (segment.distance || 0),
      0
    );
    const duration = segmentsResponse.reduce(
      (total, segment) => total + (segment.duration || 0),
      0
    );

    return {
      points,
      distance,
      duration,
    };
  }

  function buildPolylineFromSegments(segmentsResponse) {
    if (!Array.isArray(segmentsResponse) || segmentsResponse.length === 0) {
      return [];
    }

    return segmentsResponse.reduce((polyline, segment) => {
      const segmentPolyline = extractRoutePolyline(segment);

      if (segmentPolyline.length === 0) {
        return polyline;
      }

      if (polyline.length === 0) {
        return [...segmentPolyline];
      }

      const [firstPoint] = segmentPolyline;
      const lastPoint = polyline[polyline.length - 1];
      const shouldSkipFirstPoint =
        Array.isArray(firstPoint) &&
        Array.isArray(lastPoint) &&
        firstPoint[0] === lastPoint[0] &&
        firstPoint[1] === lastPoint[1];

      return [
        ...polyline,
        ...(shouldSkipFirstPoint ? segmentPolyline.slice(1) : segmentPolyline),
      ];
    }, []);
  }

  async function buildDetailedRoute(orderedAddresses) {
    if (!orderedAddresses || orderedAddresses.length < 2) {
      return {
        points: [],
        segments: [],
        distance: 0,
        duration: 0,
      };
    }

    const requests = orderedAddresses.slice(0, -1).map((item, index) =>
      fetchSegment(item, orderedAddresses[index + 1])
    );
    const segmentResponses = await Promise.all(requests);
    const rebuilt = buildRouteFromSegments(segmentResponses);
    const polyline = buildPolylineFromSegments(segmentResponses);

    return {
      points: rebuilt.points,
      distance: rebuilt.distance,
      duration: rebuilt.duration,
      polyline,
      segments: segmentResponses.map((segment) => ({
        from: segment.origin.address,
        to: segment.destination.address,
        distance: segment.distance,
        duration: segment.duration,
        coordinates: {
          from: segment.origin.coordinates,
          to: segment.destination.coordinates,
        },
        instructions: segment.instructions || [],
      })),
    };
  }

  async function fetchSegment(originAddress, destinationAddress) {
    const params = new URLSearchParams();

    params.append("origin", originAddress);
    params.append("destination", destinationAddress);
    params.append("profile", profile);

    const response = await sendRoutingRequest({
      method: "get",
      url: `/routes/info?${params.toString()}`,
    });

    return response.data;
  }

  function addWaypoint() {
    const trimmedValue = cityInput.trim();

    if (!trimmedValue) {
      return;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return;
    }

    const resolvedCity = resolveCityOrNull(trimmedValue);

    if (!resolvedCity) {
      const message = CITY_NOT_ALLOWED_MESSAGE;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    setWaypoints((currentWaypoints) => [
      ...currentWaypoints,
      createWaypoint(resolvedCity),
    ]);
    setCityInput("");
    setErrorMessage("");
  }

  function removeWaypoint(id) {
    const updatedWaypoints = waypoints.filter((item) => item.id !== id);

    setWaypoints(updatedWaypoints);

    if (routePoints.length > 0) {
      rebuildRoute(updatedWaypoints);
    }
  }

  async function optimizeRoute() {
    if (!origin.trim() || !destination.trim()) {
      setErrorMessage("Informe a cidade de origem e a cidade de destino.");
      return;
    }

    if (loadingCityCatalog) {
      setErrorMessage("Aguarde o carregamento das cidades do IBGE.");
      return;
    }

    if (cityCatalogError) {
      setErrorMessage(cityCatalogError);
      return;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin) {
      const message = `Origem: ${CITY_NOT_ALLOWED_MESSAGE}`;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    if (!resolvedDestination) {
      const message = `Destino: ${CITY_NOT_ALLOWED_MESSAGE}`;
      setErrorMessage(message);
      showValidationAlert(message);
      return;
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < waypoints.length; index += 1) {
      const item = waypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        const message = `Parada ${index + 1}: ${CITY_NOT_ALLOWED_MESSAGE}`;
        setErrorMessage(message);
        showValidationAlert(message);
        return;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    setOrigin(resolvedOrigin);
    setDestination(resolvedDestination);
    setWaypoints(resolvedWaypoints);

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const optimizeQuery = buildOptimizeQuery(
        resolvedWaypoints,
        true,
        true,
        resolvedOrigin,
        resolvedDestination
      );
      const optimizeUrl = `${getOptimizeEndpoint()}?${optimizeQuery}`;

      const response = await sendRoutingRequest({
        method: "get",
        url: optimizeUrl,
      });

      const data = response.data;
      const submittedAddresses = [
        resolvedOrigin,
        ...resolvedWaypoints.map((item) => item.address),
        resolvedDestination,
      ];
      const normalizedRoute = buildNormalizedOptimizedRoute(
        data.optimized_route || [],
        submittedAddresses
      );
      const orderedAddresses = normalizedRoute.map((item) => item.address);
      const polyline = extractRoutePolyline(data);
      const orderedStops = normalizedRoute
        .slice(1, -1)
        .map((item) => createWaypoint(item.address));

      setWaypoints(orderedStops);
      setRoutePoints(normalizedRoute);
      setRouteSegments([]);
      setRoutePolyline(polyline);
      setRouteOptimizationData({
        ...data,
        requestedOrigin: resolvedOrigin,
        requestedDestination: resolvedDestination,
        requestedWaypoints: resolvedWaypoints.map((item) => item.address),
      });
      setTotalDistance(data.total_distance || 0);
      setTotalDuration(data.total_duration || 0);

      if (orderedAddresses.length >= 2) {
        try {
          const rebuilt = await buildDetailedRoute(orderedAddresses);

          setRoutePoints(rebuilt.points);
          setRouteSegments(rebuilt.segments);
          setTotalDistance(rebuilt.distance || data.total_distance || 0);
          setTotalDuration(rebuilt.duration || data.total_duration || 0);
        } catch (detailError) {
          console.log(detailError);
        }
      }
    } catch (error) {
      console.log(error);
      setRouteOptimizationData(null);
      setErrorMessage(
        error?.response?.data?.message ||
          "Nao foi possivel otimizar a rota com a API de roteamento."
      );
    } finally {
      setLoading(false);
    }
  }

  async function rebuildRoute(nextWaypoints) {
    if (!origin.trim() || !destination.trim()) {
      return;
    }

    if (loadingCityCatalog || cityCatalogError) {
      return;
    }

    const resolvedOrigin = resolveCityOrNull(origin);
    const resolvedDestination = resolveCityOrNull(destination);

    if (!resolvedOrigin || !resolvedDestination) {
      return;
    }

    const resolvedWaypoints = [];

    for (let index = 0; index < nextWaypoints.length; index += 1) {
      const item = nextWaypoints[index];
      const resolved = resolveCityOrNull(item.address);

      if (!resolved) {
        return;
      }

      resolvedWaypoints.push({
        ...item,
        address: resolved,
      });
    }

    const orderedAddresses = [
      resolvedOrigin,
      ...resolvedWaypoints.map((item) => item.address),
      resolvedDestination,
    ];

    if (orderedAddresses.length < 2) {
      setRoutePoints([]);
      setRouteSegments([]);
      setRoutePolyline([]);
      setTotalDistance(0);
      setTotalDuration(0);
      return;
    }

    setReordering(true);
    setErrorMessage("");
    setSuccessMessage("");
    setRouteOptimizationData(null);

    try {
      const previewData = await fetchRoutePreview(resolvedWaypoints, false);
      const rebuilt = await buildDetailedRoute(orderedAddresses);
      const manualPolyline = extractRoutePolyline(previewData);

      setRouteSegments(rebuilt.segments);
      setRoutePoints(rebuilt.points);
      setRoutePolyline(
        rebuilt.polyline.length > 1
          ? rebuilt.polyline
          : manualPolyline.length > 1
          ? manualPolyline
          : []
      );
      setTotalDistance(rebuilt.distance);
      setTotalDuration(rebuilt.duration);
    } catch (error) {
      console.log(error);
      setRouteOptimizationData(null);
      setErrorMessage(
        error?.response?.data?.message ||
          "Nao foi possivel recalcular a rota com a ordem manual."
      );
    } finally {
      setReordering(false);
    }
  }

  function reorderWaypointByIds(sourceId, targetId) {
    if (!sourceId || !targetId || sourceId === targetId) {
      return;
    }

    const currentIndex = waypoints.findIndex((item) => item.id === sourceId);
    const targetIndex = waypoints.findIndex((item) => item.id === targetId);

    if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) {
      return;
    }

    const reordered = [...waypoints];
    const [movedItem] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);

    setWaypoints(reordered);
    setDraggedWaypointId(null);
    setDragOverWaypointId(null);
    rebuildRoute(reordered);
  }

  function moveWaypointToIndex(waypointId, nextPosition) {
    if (!waypointId || !Number.isInteger(nextPosition)) {
      return;
    }

    const currentIndex = waypoints.findIndex((item) => item.id === waypointId);

    if (currentIndex < 0) {
      return;
    }

    const targetIndex = Math.max(0, Math.min(waypoints.length - 1, nextPosition - 1));

    if (currentIndex === targetIndex) {
      setShowOrderModal(false);
      setSelectedWaypointForOrder(null);
      setOrderInput("");
      return;
    }

    const reordered = [...waypoints];
    const [movedItem] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);

    setWaypoints(reordered);
    setShowOrderModal(false);
    setSelectedWaypointForOrder(null);
    setOrderInput("");
    rebuildRoute(reordered);
  }

  function openOrderModal(item, index) {
    setSelectedWaypointForOrder(item);
    setOrderInput(String(index + 1));
    setShowOrderModal(true);
    setErrorMessage("");
    setSuccessMessage("");
  }

  function closeOrderModal() {
    setShowOrderModal(false);
    setSelectedWaypointForOrder(null);
    setOrderInput("");
  }

  function handleOrderSubmit(event) {
    event.preventDefault();

    if (!selectedWaypointForOrder) {
      return;
    }

    const parsedPosition = Number.parseInt(orderInput, 10);

    if (!Number.isInteger(parsedPosition) || parsedPosition < 1 || parsedPosition > waypoints.length) {
      setErrorMessage(`Informe uma posicao entre 1 e ${waypoints.length}.`);
      return;
    }

    moveWaypointToIndex(selectedWaypointForOrder.id, parsedPosition);
  }

  const waypointDistanceLabels = useMemo(
    () => routePoints.slice(1, -1).map((point) => formatDistance(point?.distance_from_previous)),
    [routePoints]
  );

  function normalizeFieldValue(setter, value) {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
      return;
    }

    const resolved = resolveCityOrNull(value);

    if (resolved) {
      setter(resolved);
      setErrorMessage("");
      return;
    }

    setErrorMessage(CITY_NOT_ALLOWED_MESSAGE);
    showValidationAlert(CITY_NOT_ALLOWED_MESSAGE);
    setter("");
  }

  const segmentLabels = routeSegments.map((segment, index) => {
    const from = segment.coordinates?.from;
    const to = segment.coordinates?.to;

    if (!isValidLatLng(from?.lat, from?.lng) || !isValidLatLng(to?.lat, to?.lng)) {
      return null;
    }

    return {
      id: `${segment.from}-${segment.to}-${index}`,
      position: [(from.lat + to.lat) / 2, (from.lng + to.lng) / 2],
      label: formatDistance(segment.distance),
    };
  });

  const pointConnectionLine = routePoints
    .filter((point) => hasValidCoordinates(point))
    .map((point) => [point.coordinates.lat, point.coordinates.lng]);

  const fallbackSegmentLabels =
    routeSegments.length > 0
      ? segmentLabels
      : routePoints.slice(1).map((point, index) => {
          const previousPoint = routePoints[index];
          const from = previousPoint?.coordinates;
          const to = point?.coordinates;

          if (!from || !to) {
            return null;
          }

          return {
            id: `${previousPoint.address}-${point.address}-${index}`,
            position: [(from.lat + to.lat) / 2, (from.lng + to.lng) / 2],
            label: formatDistance(point.distance_from_previous),
          };
        });

  function createOrderIcon(order, type) {
    const markerLabel =
      type === "origin" || type === "destination" ? "" : String(order);

    return L.divIcon({
      className: "",
      html: `<div class="route-point-marker ${type}">${markerLabel}</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
  }

  function createSegmentIcon(label) {
    return L.divIcon({
      className: "",
      html: `<div class="route-segment-label">${label}</div>`,
      iconSize: [72, 24],
      iconAnchor: [36, 12],
    });
  }

  return (
    <div className="route-page">
      <MenuLateral />

      <div className="route-content">
        <aside className="route-sidebar">
          <div className="route-header">
            <h2>Roteamento</h2>
            <p>Monte a rota, otimize a ordem e ajuste manualmente quando precisar.</p>
          </div>

          <div className="route-actions route-actions-top">
            <button
              type="button"
              className="secondary"
              onClick={openRoutesModal}
              disabled={loading || reordering || savingRoute || openingRoute}
            >
              {openingRoute ? "Carregando..." : "Abrir rotas"}
            </button>
          </div>

          <label className="route-field">
            <span>Nome da rota</span>
            <input
              type="text"
              value={routeName}
              maxLength={50}
              onChange={(event) => setRouteName(event.target.value)}
              placeholder="Ex.: Rota Centro"
            />
          </label>

          <label className="route-field">
            <span>Cidade origem</span>
            <input
              type="text"
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}
              onBlur={(event) =>
                normalizeFieldValue(setOrigin, event.target.value)
              }
              list="ibge-city-options"
              placeholder="Ex.: Cuiaba, MT"
            />
          </label>

          <label className="route-field">
            <span>Cidade destino</span>
            <input
              type="text"
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
              onBlur={(event) =>
                normalizeFieldValue(setDestination, event.target.value)
              }
              list="ibge-city-options"
              placeholder="Ex.: Rondonopolis, MT"
            />
          </label>

          <div className="route-field">
            <span>Cidade de parada</span>
            <div className="route-inline-field">
              <input
                type="text"
                value={cityInput}
                onChange={(event) => setCityInput(event.target.value)}
                onBlur={(event) =>
                  normalizeFieldValue(setCityInput, event.target.value)
                }
                list="ibge-city-options"
                placeholder="Adicionar cidade"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addWaypoint();
                  }
                }}
              />
              <button type="button" onClick={addWaypoint}>
                Adicionar
              </button>
            </div>
          </div>

          <datalist id="ibge-city-options">
            {cityResolver.options.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>

          <p className="route-helper-text">
            {loadingCityCatalog
              ? "Carregando cidades do IBGE..."
              : cityCatalogError
              ? cityCatalogError
              : `Validacao ativa com ${cityResolver.options.length} cidades do IBGE.`}
          </p>

          <div className="route-waypoints">
            <div className="route-section-title">
              <strong>Paradas</strong>
              <span>{waypoints.length} cidades</span>
            </div>

            <div className="route-waypoint-list">
              {waypoints.length === 0 ? (
                <p className="route-empty-state">
                  Nenhuma cidade adicionada ainda.
                </p>
              ) : (
                waypoints.map((item, index) => (
                  <div
                    key={item.id}
                    className={`route-waypoint-card${
                      draggedWaypointId === item.id ? " dragging" : ""
                    }${dragOverWaypointId === item.id ? " drag-over" : ""}`}
                    draggable={!loading && !reordering}
                    onDragStart={() => {
                      setDraggedWaypointId(item.id);
                      setDragOverWaypointId(item.id);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (draggedWaypointId && draggedWaypointId !== item.id) {
                        setDragOverWaypointId(item.id);
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      reorderWaypointByIds(draggedWaypointId, item.id);
                    }}
                    onDragEnd={() => {
                      setDraggedWaypointId(null);
                      setDragOverWaypointId(null);
                    }}
                  >
                    <button
                      type="button"
                      className="route-waypoint-order"
                      onDoubleClick={() => openOrderModal(item, index)}
                      disabled={loading || reordering}
                      aria-label={`Alterar posicao da cidade ${item.address}`}
                      title="Duplo clique para informar a nova posicao"
                    >
                      {index + 1}
                    </button>
                    <div className="route-waypoint-text">
                      <span className="route-waypoint-distance">
                        {waypointDistanceLabels[index] || "0 km"}
                      </span>
                      <strong>{item.address}</strong>
                    </div>
                    <div className="route-waypoint-actions">
                    <button
                      type="button"
                      className="drag-handle"
                      disabled={loading || reordering}
                      aria-label="Arrastar cidade"
                      title="Arraste para reordenar"
                    >
                      <FaGripVertical />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeWaypoint(item.id)}
                      disabled={loading || reordering}
                      aria-label="Remover cidade"
                      title="Remover"
                    >
                      <FaTimes />
                    </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="route-actions">
            <button
              type="button"
              className="primary"
              onClick={optimizeRoute}
              disabled={loading || reordering || savingRoute}
            >
              {loading ? "Roteando..." : "Rotear"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleSaveRoute}
              disabled={loading || reordering || savingRoute || openingRoute}
            >
              {savingRoute
                ? "Salvando..."
                : selectedRouteId
                ? "Alterar"
                : "Salvar"}
            </button>
          </div>

          {loading ? (
            <div className="route-loading-panel" role="status" aria-live="polite">
              <div className="route-loading-header">
                <strong>Calculando a melhor rota</strong>
                <span className="route-loading-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
              <p>
                Estamos consultando os trechos e reorganizando a ordem das cidades.
              </p>
              <div className="route-loading-bar">
                <div className="route-loading-bar-fill" />
              </div>
            </div>
          ) : null}

          <div className="route-actions">
            <button
              type="button"
              className="secondary"
              onClick={handleExportRoute}
              disabled={loading || reordering || savingRoute}
            >
              Gerar PDF/Excel
            </button>
          </div>

          <div className="route-actions">
            <button
              type="button"
              className="warning"
              onClick={handleFinishRoute}
              disabled={
                loading ||
                reordering ||
                savingRoute ||
                openingRoute ||
                selectedRouteStatus === 1
              }
            >
              {savingRoute ? "Finalizando..." : "Finalizar"}
            </button>
          </div>

          <p className="route-helper-text"></p>

          {errorMessage ? <p className="route-error">{errorMessage}</p> : null}
          {successMessage ? <p className="route-success">{successMessage}</p> : null}

          <div className="route-summary">
            <div className="route-summary-card">
              <span>Distancia total</span>
              <strong>{formatDistance(totalDistance)}</strong>
            </div>
            <div className="route-summary-card">
              <span>Tempo estimado</span>
              <strong>{formatDuration(totalDuration)}</strong>
            </div>
          </div>
        </aside>

        <section className="route-map-panel">
          <MapContainer
            center={mapCenter}
            zoom={5}
            style={{ height: "100vh", width: "100%" }}
          >
            <TileLayer
              attribution="<p>GRUPO PALMARES</p>"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {routePolyline.length > 1 ? (
              <Polyline
                positions={routePolyline}
                pathOptions={{ color: "#1d64d8", weight: 5, opacity: 0.85 }}
              />
            ) : null}

            {routePolyline.length <= 1 && pointConnectionLine.length > 1 ? (
              <Polyline
                positions={pointConnectionLine}
                pathOptions={{ color: "#1d64d8", weight: 4, opacity: 0.9 }}
              />
            ) : null}

            {routePoints.map((point, index) => {
              if (!hasValidCoordinates(point)) {
                return null;
              }

              const markerType =
                index === 0
                  ? "origin"
                  : index === routePoints.length - 1
                  ? "destination"
                  : "waypoint";

              return (
                <Marker
                  key={`${point.address}-${index}`}
                  position={[point.coordinates.lat, point.coordinates.lng]}
                  icon={createOrderIcon(index, markerType)}
                >
                  <Popup>
                    <strong>{point.address}</strong>
                    <br />
                    {markerType === "waypoint" ? `Parada ${index}` : null}
                    {markerType === "waypoint" ? <br /> : null}
                    Trecho anterior: {formatDistance(point.distance_from_previous)}
                  </Popup>
                </Marker>
              );
            })}

            {fallbackSegmentLabels.map((segment) =>
              segment ? (
                <Marker
                  key={segment.id}
                  position={segment.position}
                  icon={createSegmentIcon(segment.label)}
                />
              ) : null
            )}
          </MapContainer>
        </section>
      </div>

      {showRouteModal ? (
        <div className="route-modal-overlay">
          <div className="route-modal">
            <div className="route-modal-header">
              <h3>Selecionar rota</h3>
              <button
                type="button"
                className="route-modal-close"
                onClick={() => setShowRouteModal(false)}
              >
                x
              </button>
            </div>

            <div className="route-modal-filters">
              <button
                type="button"
                className={modalStatusFilter === 0 ? "active" : ""}
                onClick={() => changeModalFilter(0)}
                disabled={modalLoading}
              >
                Abertas
              </button>
              <button
                type="button"
                className={modalStatusFilter === 1 ? "active" : ""}
                onClick={() => changeModalFilter(1)}
                disabled={modalLoading}
              >
                Fechadas (ultimas 30)
              </button>
            </div>

            <div className="route-modal-list">
              {modalLoading ? <p>Carregando rotas...</p> : null}
              {!modalLoading && modalError ? <p>{modalError}</p> : null}
              {!modalLoading && !modalError && availableRoutes.length === 0 ? (
                <p>Nenhuma rota encontrada para o filtro selecionado.</p>
              ) : null}

              {!modalLoading && !modalError
                ? availableRoutes.map((item) => (
                    <button
                      key={item.IDrota}
                      type="button"
                      className="route-modal-item"
                      onClick={() => handleSelectRoute(item)}
                      disabled={openingRoute}
                    >
                      <div>
                        <strong>{item.nomerota || `Rota ${item.IDrota}`}</strong>
                        <span>ID: {item.IDrota}</span>
                      </div>
                      <small>
                        Alteracao:{" "}
                        {formatDateTime(
                          item.dataalteracao ||
                            item["dataalteração"] ||
                            item.datacriacao
                        )}
                      </small>
                    </button>
                  ))
                : null}
            </div>
          </div>
        </div>
      ) : null}

      {showOrderModal ? (
        <div className="route-modal-overlay" onClick={closeOrderModal}>
          <div className="route-modal route-order-modal" onClick={(event) => event.stopPropagation()}>
            <div className="route-modal-header">
              <h3>Alterar posicao</h3>
              <button
                type="button"
                className="route-modal-close"
                onClick={closeOrderModal}
              >
                x
              </button>
            </div>

            <form className="route-order-form" onSubmit={handleOrderSubmit}>
              <p className="route-order-description">
                {selectedWaypointForOrder?.address || "Cidade selecionada"}
              </p>

              <label className="route-field">
                <span>Nova ordem</span>
                <input
                  type="number"
                  min="1"
                  max={waypoints.length}
                  value={orderInput}
                  onChange={(event) => setOrderInput(event.target.value)}
                  placeholder={`Entre 1 e ${waypoints.length}`}
                  autoFocus
                />
              </label>

              <p className="route-helper-text">
                Digite uma posicao entre 1 e {waypoints.length}.
              </p>

              <div className="route-actions route-order-actions">
                <button type="button" className="secondary" onClick={closeOrderModal}>
                  Cancelar
                </button>
                <button type="submit" className="primary" disabled={loading || reordering}>
                  Mover
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Roteamento;
